import { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { useIntl } from 'react-intl';
import { t } from 'services/translation';

// https://github.com/catamphetamine/react-phone-number-input
import PhoneInput, { type Country } from 'react-phone-number-input/input';
import { Theme } from 'ui_system/theme';
import { countryCodes, getCountryCallingCode } from './countryUtils';
import { getLocalizedCountryMap } from './localizedCountryUtils';

const inputHeight = '48px';
const countrySelectWidth = '80px';

const PhoneInputs = styled.div`
  display: flex;
  height: ${inputHeight};
`;

// Overriding global input styles
const StyledPhoneInput = styled(PhoneInput)<{
  hasError?: boolean;
  errorColor?: string;
}>`
  width: calc(100% - ${countrySelectWidth}) !important;
  height: 100% !important;
  border: 1px solid
    ${(props) =>
      props.hasError
        ? props.errorColor
        : Theme.colors.previewSubtleOutlineColor} !important;
  color: ${(props) =>
    props.hasError ? props.errorColor : 'initial'} !important;
  border-left: 0 !important;
  border-radius: 0 !important;
  padding: 10px 15px !important;

  &::placeholder {
    color: ${Theme.colors.previewGuideTextColor} !important;
  }

  &:focus {
    outline: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  width: ${countrySelectWidth};
`;

const Select = styled.select`
  appearance: none;
  position: absolute;
  opacity: 0;
  width: 100% !important;
  height: 100% !important;
  border: 1px solid ${Theme.colors.previewSubtleOutlineColor};
  &:hover {
    cursor: pointer;
  }
`;

const Button = styled.button<{
  hovered?: boolean;
  hasError?: boolean;
  errorColor?: string;
}>`
  font-size: 16px;
  display: flex;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid
    ${(props) =>
      props.hasError
        ? props.errorColor
        : Theme.colors.borderActionableFieldColor};
  background-color: ${(props) =>
    props.hovered ? 'rgb(243, 243, 243)' : 'white'};
  padding: 10px;
`;

const ArrowDown = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid ${Theme.colors.previewIconColor};
  margin-right: 6px;
`;

const DEFAULT_COUNTRY = 'US';

const effectiveCountry = (cc: string | undefined | null): Country => {
  return (cc && countryCodes.includes(cc) ? cc : DEFAULT_COUNTRY) as Country;
};

interface PhoneNumberInputProps {
  phoneNumber?: string | null;
  setPhoneNumber: (value: string | null) => void;
  countryCode?: string;
  hasError?: boolean;
  errorColor?: string;
  dataTestId?: string;
  ariaLabelledBy?: string;
}

export const PhoneNumberInput = ({
  phoneNumber,
  setPhoneNumber,
  countryCode: cc = DEFAULT_COUNTRY,
  hasError = false,
  errorColor = '#ed1c24',
  dataTestId,
  ariaLabelledBy,
}: PhoneNumberInputProps) => {
  const { locale } = useIntl();
  const [hovered, setHovered] = useState(false);
  const [countryCode, setCountryCode] = useState(() => effectiveCountry(cc));
  const localizedMap = useMemo(() => getLocalizedCountryMap(locale), [locale]);

  const onCountryChange = (value: string) => {
    setCountryCode(effectiveCountry(value));
    // Swap calling codes if there is one
    const callingCode = `+${getCountryCallingCode(countryCode)}`;
    if (phoneNumber && phoneNumber.indexOf(callingCode) === 0) {
      const newCallingCode = `+${getCountryCallingCode(value)}`;
      setPhoneNumber(`${newCallingCode}${phoneNumber.split(callingCode)[1]}`);
    }
  };

  return (
    <PhoneInputs>
      <SelectContainer>
        <Button
          data-testid="phone-input-button"
          className="phone-input-button"
          hovered={hovered}
          hasError={hasError}
          errorColor={errorColor}
        >
          <ArrowDown />+{getCountryCallingCode(effectiveCountry(countryCode))}
        </Button>

        <Select
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          value={effectiveCountry(countryCode)}
          onChange={(e) => onCountryChange(e.target.value)}
          data-testid={dataTestId}
          aria-label="country code"
        >
          {Object.entries(localizedMap).map(([code, name]) => (
            <option key={code} value={code}>
              {name}
              &nbsp; +{getCountryCallingCode(code)}
            </option>
          ))}
        </Select>
      </SelectContainer>
      <StyledPhoneInput
        placeholder={t('PHONE_NUMBER')}
        country={effectiveCountry(countryCode)}
        value={phoneNumber ?? undefined}
        // Make sure it's set to null rather than undefined
        onChange={(val: string | undefined) =>
          val ? setPhoneNumber(val) : setPhoneNumber(null)
        }
        hasError={hasError}
        errorColor={errorColor}
        aria-labelledby={ariaLabelledBy}
      />
    </PhoneInputs>
  );
};
